import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Check, TrendingUp, Rocket, Briefcase, ArrowLeft, ArrowRight,
  Target, Phone, Mail, Eye, Shield, Users, Compass, Settings,
  ChevronDown, Zap, Building2, Crown, Layers
} from "lucide-react";
import { usePageSEO } from "@/hooks/usePageSEO";

const COMPARISON_TABLE = [
  { name: "Business Clarity Assessment", price: "Free", best: "Initial clarity" },
  { name: "Clarity Strategy Session", price: "$495", best: "Focused strategic guidance" },
  { name: "Business Solutions Diagnostic", price: "From $995", best: "Deeper business analysis" },
  { name: "Strategic Foundation", price: "From $3,500", best: "Strategy and infrastructure" },
  { name: "Growth & Optimization", price: "From $7,500", best: "Implementation and improvement" },
  { name: "Integrated Business Solutions", price: "From $7,500", best: "Multi-function challenges" },
  { name: "Business Transformation", price: "From $15,000", best: "Complex transformation" },
  { name: "Strategic Advisory", price: "From $4,500/mo", best: "Ongoing strategic guidance" },
  { name: "Fractional Executive", price: "From $6,500/mo", best: "Executive-level involvement" },
  { name: "Embedded Business Solutions", price: "From $9,500/mo", best: "High-touch integrated support" },
];

export function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [expandedFaq, setExpandedFaq] = useState(null);

  usePageSEO(
    "Pricing | Vertically Integrated Business Solutions | My Biz Consulting",
    "Solutions for every stage of growth. From free assessments to fractional executive leadership. Consultant-led, AI-powered, results-driven business solutions."
  );

  return (
    <div className="min-h-screen bg-white" data-testid="pricing-page">
      {/* Header */}
      <header className="bg-[#333333] text-white">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" className="text-neutral-300 hover:text-white" onClick={() => navigate(user ? "/dashboard" : "/")}>
              <ArrowLeft className="w-4 h-4 mr-1" /> {user ? "Dashboard" : "Home"}
            </Button>
            <Link to="/" className="flex items-center gap-2">
              <img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-7 brightness-0 invert" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-[#333333] text-white pb-14 pt-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-[#39FF14] text-xs font-medium uppercase tracking-widest mb-2">Consultant-Led &bull; AI-Powered &bull; Results-Driven</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">Solutions for Every Stage of Growth</h1>
          <p className="text-neutral-300 text-base max-w-2xl mx-auto mb-3">
            My Biz Consulting engagements are structured around the complexity of the problem, level of implementation
            required, and degree of strategic oversight — not simply the number of consulting hours.
          </p>
          <p className="text-neutral-500 text-sm max-w-xl mx-auto">
            Whether you need clarity around a specific challenge, help implementing a defined solution, or ongoing
            strategic leadership, we offer multiple ways to engage with our vertically integrated business solutions ecosystem.
          </p>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-4">

        {/* ─── START ─── */}
        <section className="py-14 border-b border-neutral-200">
          <div className="mb-8">
            <Badge className="bg-blue-100 text-blue-700 mb-2">Start</Badge>
            <h2 className="text-2xl font-bold text-neutral-900 mb-2">Start With Clarity</h2>
            <p className="text-neutral-600 text-sm max-w-xl">
              Not every client knows exactly what service they need. Start by identifying the real problem, priorities,
              and opportunities before investing in a larger solution.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {/* Assessment */}
            <Card className="border-neutral-200 hover:shadow-md transition-all">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Eye className="w-5 h-5 text-blue-500" />
                  <Badge variant="outline" className="text-xs border-blue-200 text-blue-600">Free</Badge>
                </div>
                <h3 className="font-bold text-neutral-900 mb-1">Business Clarity Assessment</h3>
                <p className="text-3xl font-bold text-[#2BC40E] mb-3">Free</p>
                <p className="text-xs text-neutral-600 mb-4">
                  A high-level entry point that helps identify business challenges, priorities, and potential next steps.
                </p>
                <Button onClick={() => navigate('/clarity-assessment')} className="w-full bg-neutral-900 text-white" data-testid="cta-assessment">
                  Start the Assessment <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </CardContent>
            </Card>

            {/* Strategy Session */}
            <Card className="border-neutral-200 hover:shadow-md transition-all">
              <CardContent className="p-6">
                <Compass className="w-5 h-5 text-purple-500 mb-3" />
                <h3 className="font-bold text-neutral-900 mb-1">Clarity Strategy Session</h3>
                <p className="text-3xl font-bold text-neutral-900 mb-1">$495</p>
                <p className="text-xs text-neutral-500 mb-3">60–90 minute focused session</p>
                <ul className="space-y-1.5 text-xs text-neutral-600 mb-4">
                  {["Focused review of primary challenge", "Strategic recommendations", "Priority actions", "Recommended next steps"].map(f => (
                    <li key={f} className="flex items-start gap-1.5"><Check className="w-3 h-3 text-[#39FF14] mt-0.5 flex-shrink-0" />{f}</li>
                  ))}
                </ul>
                <Button onClick={() => navigate('/clarity-assessment')} className="w-full bg-neutral-900 text-white">Book a Strategy Session</Button>
              </CardContent>
            </Card>

            {/* Diagnostic */}
            <Card className="border-neutral-200 hover:shadow-md transition-all">
              <CardContent className="p-6">
                <Target className="w-5 h-5 text-amber-500 mb-3" />
                <h3 className="font-bold text-neutral-900 mb-1">Business Solutions Diagnostic</h3>
                <p className="text-3xl font-bold text-neutral-900 mb-1">From $995</p>
                <p className="text-xs text-neutral-500 mb-3">Deeper multi-area analysis</p>
                <ul className="space-y-1.5 text-xs text-neutral-600 mb-4">
                  {["Business challenge review", "Operational & systems analysis", "Technology & workflow considerations", "Growth opportunities", "Recommended solution path", "Action roadmap"].map(f => (
                    <li key={f} className="flex items-start gap-1.5"><Check className="w-3 h-3 text-[#39FF14] mt-0.5 flex-shrink-0" />{f}</li>
                  ))}
                </ul>
                <Button onClick={() => navigate('/clarity-assessment')} className="w-full bg-neutral-900 text-white">Request a Diagnostic</Button>
                <p className="text-[10px] text-neutral-400 mt-2 text-center">Complex organizations may require a more comprehensive diagnostic beginning at approximately $1,500.</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ─── BUILD ─── */}
        <section className="py-14 border-b border-neutral-200">
          <div className="mb-8">
            <Badge className="bg-emerald-100 text-emerald-700 mb-2">Build</Badge>
            <h2 className="text-2xl font-bold text-neutral-900 mb-2">Build the Foundation</h2>
            <p className="text-neutral-600 text-sm max-w-xl">For businesses that need more than advice and are ready to create stronger systems, priorities, and infrastructure.</p>
          </div>
          <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50/50 to-white">
            <CardContent className="p-6 sm:p-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="flex-1">
                  <Settings className="w-6 h-6 text-emerald-600 mb-3" />
                  <h3 className="text-xl font-bold text-neutral-900 mb-1">Strategic Foundation</h3>
                  <p className="text-3xl font-bold text-neutral-900 mb-3">From $3,500</p>
                  <p className="text-sm text-neutral-600 mb-4">A defined strategy and infrastructure engagement. Scope may include:</p>
                  <div className="grid sm:grid-cols-2 gap-1.5">
                    {["Business assessment", "Strategic priorities", "Growth planning", "Business model review", "Workflow & process review", "Technology assessment", "Operations review", "Marketing & revenue alignment", "90-day execution roadmap", "Implementation recommendations"].map(f => (
                      <p key={f} className="flex items-start gap-1.5 text-xs text-neutral-600"><Check className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />{f}</p>
                    ))}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <Button onClick={() => navigate('/clarity-assessment')} className="bg-neutral-900 text-white h-12 px-6">
                    Build Your Foundation <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* ─── OPTIMIZE ─── */}
        <section className="py-14 border-b border-neutral-200">
          <div className="mb-8">
            <Badge className="bg-amber-100 text-amber-700 mb-2">Optimize</Badge>
            <h2 className="text-2xl font-bold text-neutral-900 mb-2">Optimize and Execute</h2>
            <p className="text-neutral-600 text-sm max-w-xl">For businesses that have identified the problem and need help connecting strategy to implementation.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {/* Growth & Optimization */}
            <Card className="border-neutral-200">
              <CardContent className="p-6">
                <TrendingUp className="w-5 h-5 text-amber-500 mb-3" />
                <h3 className="font-bold text-neutral-900 mb-1">Growth & Optimization</h3>
                <p className="text-3xl font-bold text-neutral-900 mb-1">From $7,500</p>
                <p className="text-xs text-neutral-500 mb-3">60–90 day implementation-focused</p>
                <p className="text-xs text-neutral-600 mb-4">Scope may include strategy refinement, operations improvement, systems development, workflow optimization, SOP development, technology integration, AI & automation, marketing alignment, implementation management, and KPI development.</p>
                <Button onClick={() => navigate('/clarity-assessment')} className="w-full bg-neutral-900 text-white">Explore Growth & Optimization</Button>
                <p className="text-[10px] text-neutral-400 mt-2 text-center">Final pricing depends on business functions involved and implementation complexity.</p>
              </CardContent>
            </Card>

            {/* Integrated Solutions */}
            <Card className="border-[#39FF14]/20 ring-1 ring-[#39FF14]/20">
              <CardContent className="p-6">
                <Layers className="w-5 h-5 text-[#2BC40E] mb-3" />
                <h3 className="font-bold text-neutral-900 mb-1">Integrated Business Solutions</h3>
                <p className="text-3xl font-bold text-neutral-900 mb-1">From $7,500</p>
                <p className="text-xs text-neutral-500 mb-3">Multi-function project engagements</p>
                <p className="text-xs text-neutral-600 mb-4">For challenges that cannot be solved within a single department. My Biz Consulting can coordinate strategy, operations, technology, AI, marketing, people, and implementation within one solution architecture.</p>
                <Button onClick={() => navigate('/clarity-assessment')} className="w-full bg-[#333333] text-white">Discuss Your Project</Button>
              </CardContent>
            </Card>

            {/* Transformation */}
            <Card className="border-neutral-200 bg-gradient-to-br from-neutral-50 to-white">
              <CardContent className="p-6">
                <Rocket className="w-5 h-5 text-red-500 mb-3" />
                <h3 className="font-bold text-neutral-900 mb-1">Business Transformation</h3>
                <p className="text-3xl font-bold text-neutral-900 mb-1">From $15,000</p>
                <p className="text-xs text-neutral-500 mb-3">Complex cross-functional engagements</p>
                <p className="text-xs text-neutral-600 mb-4">For larger or more complex engagements requiring significant cross-functional strategy and implementation across restructuring, modernization, AI integration, growth infrastructure, and executive oversight.</p>
                <Button onClick={() => navigate('/clarity-assessment')} className="w-full bg-neutral-900 text-white">Request a Transformation Consultation</Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ─── SCALE ─── */}
        <section className="py-14 border-b border-neutral-200">
          <div className="mb-8">
            <Badge className="bg-purple-100 text-purple-700 mb-2">Scale</Badge>
            <h2 className="text-2xl font-bold text-neutral-900 mb-2">Scale With Strategic Leadership</h2>
            <p className="text-neutral-600 text-sm max-w-xl">For businesses that need ongoing strategic direction, executive-level support, accountability, and implementation oversight.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {/* Advisory */}
            <Card className="border-neutral-200">
              <CardContent className="p-6">
                <Compass className="w-5 h-5 text-purple-500 mb-3" />
                <h3 className="font-bold text-neutral-900 mb-1">Strategic Advisory</h3>
                <p className="text-3xl font-bold text-neutral-900 mb-1">From $4,500<span className="text-base font-normal text-neutral-500">/mo</span></p>
                <p className="text-xs text-neutral-500 mb-3">Ongoing strategic guidance</p>
                <p className="text-xs text-neutral-600 mb-4">Executive advisory, strategy reviews, leadership support, performance review, priority management, decision support, accountability, and recurring planning.</p>
                <Button onClick={() => navigate('/clarity-assessment')} className="w-full bg-neutral-900 text-white">Explore Strategic Advisory</Button>
              </CardContent>
            </Card>

            {/* Fractional */}
            <Card className="border-purple-200 ring-1 ring-purple-200">
              <CardContent className="p-6">
                <Crown className="w-5 h-5 text-purple-600 mb-3" />
                <h3 className="font-bold text-neutral-900 mb-1">Fractional Executive Leadership</h3>
                <p className="text-3xl font-bold text-neutral-900 mb-1">From $6,500<span className="text-base font-normal text-neutral-500">/mo</span></p>
                <p className="text-xs text-neutral-500 mb-3">Executive-level involvement</p>
                <p className="text-xs text-neutral-600 mb-4">Fractional executive leadership, cross-functional coordination, implementation leadership, KPI management, technology & process alignment, team accountability, and growth planning.</p>
                <Button onClick={() => navigate('/clarity-assessment')} className="w-full bg-[#333333] text-white">Explore Fractional Leadership</Button>
              </CardContent>
            </Card>

            {/* Embedded */}
            <Card className="border-neutral-200 bg-[#333333] text-white">
              <CardContent className="p-6">
                <Building2 className="w-5 h-5 text-[#39FF14] mb-3" />
                <h3 className="font-bold text-white mb-1">Embedded Business Solutions</h3>
                <p className="text-3xl font-bold text-white mb-1">From $9,500<span className="text-base font-normal text-neutral-400">/mo</span></p>
                <p className="text-xs text-neutral-400 mb-3">Highest-touch recurring engagement</p>
                <p className="text-xs text-neutral-300 mb-4">Executive strategy, operations, technology, AI, marketing, business development, staffing coordination, project implementation, performance management, and specialist oversight.</p>
                <Button onClick={() => navigate('/clarity-assessment')} className="w-full bg-[#39FF14] text-black hover:bg-[#32E010]">Discuss an Embedded Engagement</Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ─── COMPARISON TABLE ─── */}
        <section className="py-14 border-b border-neutral-200">
          <h2 className="text-xl font-bold text-neutral-900 mb-6 text-center">Engagement Overview</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-neutral-200">
                  <th className="text-left py-3 px-4 font-semibold text-neutral-900">Engagement</th>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-900">Investment</th>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-900">Best For</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_TABLE.map((row, i) => (
                  <tr key={row.name} className={`border-b border-neutral-100 ${i % 2 === 0 ? "bg-neutral-50" : ""}`}>
                    <td className="py-2.5 px-4 font-medium text-neutral-900">{row.name}</td>
                    <td className="py-2.5 px-4 font-semibold text-[#2BC40E]">{row.price}</td>
                    <td className="py-2.5 px-4 text-neutral-600">{row.best}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ─── START WHERE YOU ARE ─── */}
        <section className="py-14 border-b border-neutral-200">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-xl font-bold text-neutral-900 mb-3">Start Where You Are</h2>
            <p className="text-neutral-600 text-sm">
              You don&apos;t need a five-figure engagement to begin working with My Biz Consulting. Start with an assessment,
              strategy session, diagnostic, or focused program. As your needs become more complex, our integrated business
              solutions model allows support to expand without requiring you to rebuild your advisory team from scratch.
            </p>
          </div>
        </section>

        {/* ─── MORE THAN HOURS ─── */}
        <section className="py-14 border-b border-neutral-200">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-xl font-bold text-neutral-900 mb-3">More Than Consulting Hours</h2>
            <p className="text-neutral-600 text-sm max-w-2xl mx-auto mb-6">
              Traditional consulting often separates strategy from execution. My Biz Consulting is structured differently.
              Our vertically integrated model connects strategy, operations, systems, technology, AI, marketing, people,
              and implementation within one coordinated ecosystem.
            </p>
            <div className="flex flex-wrap justify-center gap-2 mb-4">
              {["Clarity", "Strategy", "Systems", "Technology", "People", "Execution", "Growth"].map((s, i) => (
                <div key={s} className="flex items-center">
                  <span className="px-3 py-1.5 bg-neutral-100 rounded-lg text-xs font-medium text-neutral-700">{s}</span>
                  {i < 6 && <ArrowRight className="w-3 h-3 text-[#39FF14] mx-1" />}
                </div>
              ))}
            </div>
            <p className="text-xs text-neutral-500">Your engagement can expand as the problem requires without forcing you to manage disconnected consultants, agencies, tools, and vendors.</p>
          </div>
        </section>

        {/* ─── HOURLY ADVISORY ─── */}
        <section className="py-14 border-b border-neutral-200">
          <h2 className="text-xl font-bold text-neutral-900 mb-6">Hourly Advisory Services</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { title: "Principal Advisory", subtitle: "With Jamilah N. Lawry", price: "$225/hr" },
              { title: "Senior Consulting & Strategy", price: "$175–$195/hr" },
              { title: "Specialist Implementation", price: "From $150/hr" },
            ].map(h => (
              <div key={h.title} className="p-4 bg-neutral-50 rounded-xl border border-neutral-200 text-center">
                <p className="font-semibold text-neutral-900 text-sm">{h.title}</p>
                {h.subtitle && <p className="text-[10px] text-[#2BC40E]">{h.subtitle}</p>}
                <p className="text-xl font-bold text-neutral-900 mt-2">{h.price}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-neutral-500 mt-3">Hourly advisory is available when a client has a clearly defined need. Most larger engagements are structured around outcomes, projects, implementation, or ongoing strategic leadership.</p>
        </section>

        {/* ─── FAQ ─── */}
        <section className="py-14 border-b border-neutral-200">
          <h2 className="text-xl font-bold text-neutral-900 mb-6">Pricing Questions</h2>
          <div className="space-y-2 max-w-3xl">
            {[
              { q: 'Why does My Biz Consulting use "starting at" pricing?', a: "Business challenges vary in complexity. A workflow problem affecting one department is different from an operational challenge involving technology, staffing, marketing, and leadership. Starting prices allow us to provide transparency while building a scope that reflects the actual work required." },
              { q: "Do you offer hourly consulting?", a: "Yes. Hourly advisory and specialist services are available when a client has a clearly defined need. Most larger engagements are structured around outcomes, projects, implementation, or ongoing strategic leadership rather than consulting hours alone." },
              { q: "Do I need to know which service I need?", a: "No. Many clients come to us because they know something is not working but are unsure where the real problem begins. The Business Clarity Assessment or Business Solutions Diagnostic can help identify the right starting point." },
            ].map((faq, i) => (
              <div key={i} className="border border-neutral-200 rounded-lg overflow-hidden">
                <button onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-4 text-left">
                  <span className="font-medium text-sm text-neutral-900">{faq.q}</span>
                  <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${expandedFaq === i ? "rotate-180" : ""}`} />
                </button>
                {expandedFaq === i && (
                  <div className="px-4 pb-4 text-sm text-neutral-600">{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* ─── FINAL CTA ─── */}
        <section className="py-16">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-neutral-900 mb-3">Not Sure Where to Start?</h2>
            <p className="text-neutral-600 text-sm mb-8">
              You don&apos;t need to choose the perfect package before speaking with us. Start by helping us understand
              what is happening inside your business, and we&apos;ll help determine the most appropriate next step.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <Button size="lg" onClick={() => navigate('/clarity-assessment')} className="bg-neutral-900 text-white h-14 px-8">
                Start With a Business Clarity Assessment <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <a href="tel:215-443-1076">
                <Button size="lg" variant="outline" className="h-14 px-6 border-neutral-300">
                  <Phone className="w-4 h-4 mr-2" /> Talk With My Biz Consulting
                </Button>
              </a>
            </div>
            <p className="text-xs text-neutral-400 uppercase tracking-wider">Vertically Integrated Business Solutions</p>
            <p className="text-[#2BC40E] text-xs font-medium tracking-wider mt-1">Consultant-Led &bull; AI-Powered &bull; Results-Driven</p>
          </div>
        </section>
      </main>
    </div>
  );
}

export default PricingPage;
